import { DatePipe } from '@angular/common'
import { Component, DestroyRef, ViewEncapsulation, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import {
    faSpinner,
    faChevronDown,
    faChevronRight,
    faCamera,
    faXmark,
    faPaperPlane,
    faSave,
    faRotateLeft,
} from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable, forkJoin, of } from 'rxjs'

import { FeatureType, JobHazardAnalysis, JhaChecklistItem, ListItem, resolveCanonicalListValue } from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { RepositoryModal } from '../../shared/modals/base/repository-modal'
import { JobHazardAnalysisListComponent } from './job-hazard-analysis-list.component'

interface PendingPhoto {
    file: File
    url: string
    type: string
}

@Component({
    templateUrl: './job-hazard-analysis-modal.component.html',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, FaIconComponent, WjInputModule, VoiceTextFieldComponent, DatePipe],
    styles: [
        // Override the global `.modal-footer { margin-top: -1em }` rule in styles.css for this
        // tall scrollable modal so the footer doesn't overlap the bottom of the scrolling body.
        `
            .jha-modal-footer {
                margin-top: 0 !important;
            }
            .jha-photo-drop-zone {
                width: 110px;
                height: 110px;
                border: 2px dashed #ccc;
                cursor: pointer;
                font-size: 11px;
                text-align: center;
                padding: 4px;
            }
            .jha-photo-drop-zone-active {
                border-color: #0d6efd;
                background-color: #e7f1ff;
            }
        `,
    ],
})
export class JobHazardAnalysisModalComponent extends RepositoryModal<JobHazardAnalysis> {
    private destroyRef = inject(DestroyRef)

    faSpinner = faSpinner
    faChevronDown = faChevronDown
    faChevronRight = faChevronRight
    faCamera = faCamera
    faXmark = faXmark
    faPaperPlane = faPaperPlane
    faSave = faSave
    faRotateLeft = faRotateLeft

    jobMap: ListItem<string>[] = []
    userMap: ListItem<number>[] = []
    inspectorMap: ListItem<string>[] = []
    /** Combo value: `e:{hrRef}` for an employee, `v:{vendorId}` for a vendor account. */
    inspectorKey = ''
    vendorNames: string[] = []

    collapsedSections: boolean[] = []
    expandedItemIds = new Set<string>()
    dragOverItemId: string | null = null
    dragOverGeneralPhotos = false
    subcontractorInput = ''
    subcontractorChips: string[] = []

    /** Pending uploads keyed by checklist item id (server still hasn't seen these). */
    private pendingUploadsByItemId = new Map<string, PendingPhoto[]>()
    /** Existing image URLs (relative to JobHazardAnalysisItem folder) marked for deletion on save. */
    private deletedExistingByItemId = new Map<string, Set<string>>()
    /** Pending form-level photos (not tied to a checklist item). */
    private pendingGeneralPhotos: PendingPhoto[] = []
    /** Existing general image URLs marked for deletion on save. */
    private deletedGeneralUrls = new Set<string>()

    /**
     * Submit (vs Save Draft) determines whether status goes to "Submitted". We capture the choice
     * here so the shared `save()` flow in BaseModal can route through one onSave override.
     */
    private submitOnSave = false

    /**
     * When true, only non-compliant rows are shown (superintendent / field correction workflow).
     * Optional query param `jhaCorrectionFocus=1` from email links.
     */
    correctionFocusMode = false

    protected onInitialized() {
        const parent = this.editOptions.initializeParams as JobHazardAnalysisListComponent
        this.jobMap = parent.jobMap
        this.userMap = parent.userMap
        this.inspectorMap = parent.inspectorMap
        this.vendorNames = parent.vendorNames
        this.correctionFocusMode = !!parent.openJhaCorrectionFocus

        const canonicalJob = resolveCanonicalListValue(this.target.job, this.jobMap.map(j => j.value))
        if (canonicalJob != null) {
            this.target.job = canonicalJob
        }

        // The list component holds either a freshly-built JHA (createNew) or one that came from
        // GetEntities -- which already includes the full section/item graph and image URLs.
        this.collapsedSections = this.target.sections.map(() => false)
        this.subcontractorChips = this.target.subcontractorList

        if (this.isAdding) {
            this.preselectCurrentInspector()
        }
        this.inspectorKey = this.inspectorKeyFromTarget()

        const focusId = parent.focusJhaItemId
        if (focusId) {
            queueMicrotask(() => this.focusChecklistItemById(focusId))
        }
    }

    get canEditJha(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyJobHazardAnalysisEdit])
    }

    /** The inspector chosen on this report — an employee or a vendor account — may revert "corrected" items. */
    get isAssignedSafetyInspector(): boolean {
        const vendorId = this.target.inspectorVendorId
        if (vendorId != null && vendorId > 0) {
            return this.vendorIdFromUsername(this.services.authentication.username()) === vendorId
        }

        const hr = this.services.authentication.hrref
        return hr != null && hr > 0 && this.target.inspectorHrRef === hr
    }

    get showCorrectionFocusChrome(): boolean {
        return this.canEditJha && this.target.status === 'Submitted'
    }

    sectionHasNonCompliant(section: { items: JhaChecklistItem[] }): boolean {
        return section.items.some(i => i.response === 'Non-Compliant')
    }

    displayNameForHrRef(hrRef: number | null | undefined): string {
        if (hrRef == null || hrRef <= 0) return ''
        const m = this.userMap.find(u => u.value === hrRef)
        return m?.display ?? ''
    }

    lastMarkedCorrectActorName(item: JhaChecklistItem): string {
        const hist = item.correctionHistory ?? []
        for (let k = hist.length - 1; k >= 0; k--) {
            if (hist[k].eventType === 'MarkedCorrected') {
                return this.displayNameForHrRef(hist[k].actorHrRef)
            }
        }
        return ''
    }

    eventVerb(e: { eventType: string }): string {
        switch (e.eventType) {
            case 'MarkedCorrected':
                return 'Marked corrected'
            case 'RevertedByInspector':
                return 'Marked not corrected (by inspector)'
            default:
                return e.eventType
        }
    }

    revertInspectorCorrection(item: JhaChecklistItem) {
        if (!this.isAssignedSafetyInspector) return
        item.corrected = ''
        this.saveDraft()
    }

    private focusChecklistItemById(itemId: string) {
        for (let i = 0; i < this.target.sections.length; i++) {
            const sec = this.target.sections[i]
            if (sec.items.some(it => String(it.id).toLowerCase() === String(itemId).toLowerCase())) {
                this.collapsedSections[i] = false
                break
            }
        }
        setTimeout(() => {
            document.getElementById('jha-item-' + itemId.toLowerCase())?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 0)
    }

    onJobSelected() {
        const job = this.jobMap.find(j => j.value === this.target.job)
        this.target.jobName = job ? job.display : ''
    }

    onInspectorSelected() {
        this.applyInspectorKey(this.inspectorKey)
    }

    private preselectCurrentInspector() {
        const auth = this.services.authentication
        if (auth.isLessor()) {
            const vendorId = this.vendorIdFromUsername(auth.username())
            const key = vendorId == null ? '' : `v:${vendorId}`
            if (key && this.inspectorMap.some(i => i.value === key)) {
                this.target.inspectorVendorId = vendorId
                this.target.inspectorHrRef = null
                this.target.inspectorName = this.inspectorMap.find(i => i.value === key)?.display ?? ''
            }
            return
        }

        const currentUserId = auth.hrref
        const key = `e:${currentUserId}`
        if (currentUserId && this.inspectorMap.some(i => i.value === key)) {
            this.target.inspectorHrRef = currentUserId
            this.target.inspectorVendorId = null
            this.target.inspectorName = this.inspectorMap.find(i => i.value === key)?.display ?? ''
        }
    }

    private inspectorKeyFromTarget(): string {
        const vendorId = this.target.inspectorVendorId
        if (vendorId != null && vendorId > 0) return `v:${vendorId}`
        const hrRef = this.target.inspectorHrRef
        if (hrRef != null && hrRef > 0) return `e:${hrRef}`
        return ''
    }

    private applyInspectorKey(key: string | null) {
        const match = key ? this.inspectorMap.find(i => i.value === key) : undefined
        this.target.inspectorName = match?.display ?? ''

        if (key?.startsWith('v:')) {
            const id = Number(key.slice(2))
            this.target.inspectorVendorId = Number.isInteger(id) && id > 0 ? id : null
            this.target.inspectorHrRef = null
            return
        }

        if (key?.startsWith('e:')) {
            const id = Number(key.slice(2))
            this.target.inspectorHrRef = Number.isInteger(id) && id > 0 ? id : null
            this.target.inspectorVendorId = null
            return
        }

        this.target.inspectorHrRef = null
        this.target.inspectorVendorId = null
    }

    private vendorIdFromUsername(username: string): number | null {
        if (!username || username.length < 2) return null
        const lower = username.toLowerCase()
        if (lower[0] !== 'v' || lower[1] === 'c') return null
        const id = Number(lower.slice(1))
        return Number.isInteger(id) && id > 0 ? id : null
    }

    toggleSection(index: number) {
        this.collapsedSections[index] = !this.collapsedSections[index]
    }

    onNotApplicableChanged(sectionIndex: number) {
        if (this.target.sections[sectionIndex].notApplicable) {
            this.collapsedSections[sectionIndex] = true
        }
    }

    addSubcontractor() {
        const name = this.subcontractorInput.trim()
        if (name && !this.subcontractorChips.includes(name)) {
            this.subcontractorChips.push(name)
        }
        this.subcontractorInput = ''
    }

    removeSubcontractor(index: number) {
        this.subcontractorChips.splice(index, 1)
    }

    onSubcontractorKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault()
            this.addSubcontractor()
        }
    }

    /* ------------------------- Per-item photo handling ------------------------- */

    isItemExpanded(itemId: string): boolean {
        return this.expandedItemIds.has(itemId)
    }

    toggleItemPhotos(itemId: string) {
        if (this.expandedItemIds.has(itemId)) {
            this.expandedItemIds.delete(itemId)
        } else {
            this.expandedItemIds.add(itemId)
        }
    }

    pendingPhotosFor(itemId: string): PendingPhoto[] {
        return this.pendingUploadsByItemId.get(itemId) ?? []
    }

    visibleExistingFor(item: JhaChecklistItem): string[] {
        const removed = this.deletedExistingByItemId.get(item.id)
        if (!removed?.size) return item.imageUrls ?? []
        return (item.imageUrls ?? []).filter(u => !removed.has(u))
    }

    photoCountFor(item: JhaChecklistItem): number {
        return this.visibleExistingFor(item).length + this.pendingPhotosFor(item.id).length
    }

    async onItemPhotoChanged(item: JhaChecklistItem, event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files?.length) return
        await this.addPhotosFromFiles(item.id, input.files, this.pendingUploadsByItemId)
        input.value = ''
    }

    onItemPhotoDrop(item: JhaChecklistItem, event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverItemId = null
        const files = event.dataTransfer?.files
        if (files?.length) void this.addPhotosFromFiles(item.id, files, this.pendingUploadsByItemId)
    }

    onItemPhotoDragOver(item: JhaChecklistItem, event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverItemId = item.id
    }

    onItemPhotoDragLeave(event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverItemId = null
    }

    private async addPhotosFromFiles(
        itemId: string,
        files: FileList,
        targetMap: Map<string, PendingPhoto[]>,
    ) {
        const list = targetMap.get(itemId) ?? []
        const options: Options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true }
        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            if (!file.type.startsWith('image/')) continue
            try {
                const compressed = await imageCompression(file, options)
                const url = await imageCompression.getDataUrlFromFile(compressed)
                list.push({ file: compressed, url, type: compressed.type || file.type })
            } catch {
                list.push({ file, url: '', type: file.type })
            }
        }
        if (list.length) targetMap.set(itemId, list)
    }

    removePendingPhoto(itemId: string, photo: PendingPhoto) {
        const list = this.pendingUploadsByItemId.get(itemId)
        if (!list) return
        const idx = list.indexOf(photo)
        if (idx >= 0) list.splice(idx, 1)
    }

    removeExistingPhoto(item: JhaChecklistItem, url: string) {
        let removed = this.deletedExistingByItemId.get(item.id)
        if (!removed) {
            removed = new Set<string>()
            this.deletedExistingByItemId.set(item.id, removed)
        }
        removed.add(url)
    }

    existingPhotoSrc(url: string): string {
        return './Resources/Images/JobHazardAnalysisItem/' + url + '?w=300'
    }

    openExistingPhoto(url: string) {
        window.open('./Resources/Images/JobHazardAnalysisItem/' + url, '_blank')
    }

    /* ------------------------- General (form-level) photo handling ------------------------- */

    visibleGeneralExisting(): string[] {
        if (!this.deletedGeneralUrls.size) return this.target.generalImageUrls ?? []
        return (this.target.generalImageUrls ?? []).filter(u => !this.deletedGeneralUrls.has(u))
    }

    pendingGeneralPhotosFor(): PendingPhoto[] {
        return this.pendingGeneralPhotos
    }

    async onGeneralPhotoChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files?.length) return
        await this.addGeneralPhotosFromFiles(input.files)
        input.value = ''
    }

    onGeneralPhotoDrop(event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverGeneralPhotos = false
        const files = event.dataTransfer?.files
        if (files?.length) void this.addGeneralPhotosFromFiles(files)
    }

    onGeneralPhotoDragOver(event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverGeneralPhotos = true
    }

    onGeneralPhotoDragLeave(event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverGeneralPhotos = false
    }

    removePendingGeneralPhoto(photo: PendingPhoto) {
        const idx = this.pendingGeneralPhotos.indexOf(photo)
        if (idx >= 0) this.pendingGeneralPhotos.splice(idx, 1)
    }

    removeExistingGeneralPhoto(url: string) {
        this.deletedGeneralUrls.add(url)
    }

    existingGeneralPhotoSrc(url: string): string {
        return './Resources/Images/JobHazardAnalysis/' + url + '?w=300'
    }

    openExistingGeneralPhoto(url: string) {
        window.open('./Resources/Images/JobHazardAnalysis/' + url, '_blank')
    }

    private async addGeneralPhotosFromFiles(files: FileList) {
        const options: Options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true }
        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            if (!file.type.startsWith('image/')) continue
            try {
                const compressed = await imageCompression(file, options)
                const url = await imageCompression.getDataUrlFromFile(compressed)
                this.pendingGeneralPhotos.push({ file: compressed, url, type: compressed.type || file.type })
            } catch {
                this.pendingGeneralPhotos.push({ file, url: '', type: file.type })
            }
        }
    }

    /* ------------------------- Save flow ------------------------- */

    saveDraft() {
        this.submitOnSave = false
        if (this.target.status !== 'Submitted') {
            this.target.status = 'Draft'
        }
        this.save()
    }

    submit() {
        this.submitOnSave = true
        this.target.status = 'Submitted'
        if (!this.target.signedOn) this.target.signedOn = new Date()
        if (!this.target.submittedOn) this.target.submittedOn = new Date()
        this.save()
    }

    protected override onBeforeSave(): boolean {
        if (!this.target.job) {
            this.services.notification.warning('Job is required')
            return false
        }
        if (this.submitOnSave) {
            const missingResponses = this.findItemsMissingResponses()
            if (missingResponses > 0) {
                const proceed = confirm(
                    `${missingResponses} checklist item(s) have no Response selected. Submit anyway?`,
                )
                if (!proceed) return false
            }
        }
        // Persist subcontractor chips into the model field that the DTO carries.
        this.target.subcontractorList = this.subcontractorChips
        return true
    }

    private findItemsMissingResponses(): number {
        let count = 0
        for (const section of this.target.sections) {
            if (section.notApplicable) continue
            for (const item of section.items) {
                if (!item.response) count++
            }
        }
        return count
    }

    protected override onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: savedDto => {
                this.uploadPendingPhotos(savedDto).subscribe({
                    next: () =>
                        this.uploadPendingGeneralPhotos(savedDto).subscribe({
                            next: () =>
                                this.deletePhotosMarkedForRemoval().subscribe({
                                    next: () => resolveSave(savedDto),
                                    error: e => rejectSave(e),
                                }),
                            error: e => rejectSave(e),
                        }),
                    error: e => rejectSave(e),
                })
            },
            error: e => rejectSave(e),
        })
    }

    /**
     * Walk the saved DTO so we can map the optimistic client-side item IDs to the server's
     * persisted IDs (they're the same because we generate Guids client-side, but defensively
     * we use the server's for the upload).
     */
    private uploadPendingPhotos(savedDto: any): Observable<unknown> {
        const calls: Observable<unknown>[] = []
        const serverItemIds = this.collectServerItemIds(savedDto)
        for (const [itemId, pending] of this.pendingUploadsByItemId.entries()) {
            if (!pending.length) continue
            if (!serverItemIds.has(itemId)) continue
            const formData = new FormData()
            formData.append('id', itemId)
            pending.forEach((p, i) => formData.append('myFile' + i, p.file, p.file.name))
            calls.push(this.services.data.post('api/JobHazardAnalysis/UploadImages', formData))
        }
        return calls.length === 0 ? of(null) : forkJoin(calls)
    }

    private uploadPendingGeneralPhotos(savedDto: any): Observable<unknown> {
        if (!this.pendingGeneralPhotos.length) return of(null)
        const jhaId = savedDto?.id ?? this.target.id
        if (!jhaId) return of(null)
        const formData = new FormData()
        formData.append('id', jhaId)
        this.pendingGeneralPhotos.forEach((p, i) => formData.append('myFile' + i, p.file, p.file.name))
        return this.services.data.post('api/JobHazardAnalysis/UploadGeneralImages', formData)
    }

    private deletePhotosMarkedForRemoval(): Observable<unknown> {
        const calls: Observable<unknown>[] = []
        for (const [, removed] of this.deletedExistingByItemId.entries()) {
            for (const url of removed) {
                const imageId = JobHazardAnalysisModalComponent.imageIdFromUrl(url)
                if (!imageId) continue
                calls.push(
                    this.services.data.http.delete(
                        `api/JobHazardAnalysis/DeleteImage?imageId=${encodeURIComponent(imageId)}`,
                    ),
                )
            }
        }
        for (const url of this.deletedGeneralUrls) {
            const imageId = JobHazardAnalysisModalComponent.imageIdFromUrl(url)
            if (!imageId) continue
            calls.push(
                this.services.data.http.delete(
                    `api/JobHazardAnalysis/DeleteImage?imageId=${encodeURIComponent(imageId)}`,
                ),
            )
        }
        return calls.length === 0 ? of(null) : forkJoin(calls)
    }

    private collectServerItemIds(savedDto: any): Set<string> {
        const ids = new Set<string>()
        for (const s of savedDto?.sections ?? []) {
            for (const i of s.items ?? []) {
                if (i?.id) ids.add(i.id)
            }
        }
        return ids
    }

    private static imageIdFromUrl(url: string): string | null {
        // URL format: <itemId>/<imageId>.<ext>
        const parts = url.split('/').filter(Boolean)
        if (parts.length < 2) return null
        const file = parts[1]
        const dot = file.lastIndexOf('.')
        const idPart = dot > 0 ? file.slice(0, dot) : file
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idPart) ? idPart : null
    }
}
